import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  TrendingDown, 
  Clock, 
  Settings, 
  Cpu, 
  Globe,
  CheckCircle,
  Zap
} from 'lucide-react';
import { CapexCalculator } from '../components/calculator/CapexCalculator';

const advantages = [
  {
    icon: TrendingDown,
    title: 'Reduce Upfront CAPEX',
    description: 'Convert capital expenditure into predictable operating expense. Our flexible rental model eliminates the need for heavy upfront investment, improving cash flow and financial flexibility.',
    benefits: ['No large initial investment', 'Predictable monthly costs', 'Improved working capital', 'Better ROI tracking']
  },
  {
    icon: Clock,
    title: 'Faster Deployment',
    description: 'Pre-engineered, modular systems reduce setup time significantly. Go from empty shell to operational warehouse in weeks, not months.',
    benefits: ['Rapid installation', 'Pre-configured systems', 'Minimal site disruption', 'Faster time-to-market']
  },
  {
    icon: Settings,
    title: 'Integrated Infrastructure Partner',
    description: 'Beyond fit-outs — we provide comprehensive solutions including automation readiness, energy upgrades, security systems, and lifecycle support.',
    benefits: ['Single point of contact', 'End-to-end solutions', 'Seamless integration', 'Ongoing support']
  },
  {
    icon: Cpu,
    title: 'Technology-Driven',
    description: 'Our infrastructure is designed for the future. IoT-ready, automation-compatible, and built to integrate with modern warehouse management systems.',
    benefits: ['IoT sensor integration', 'ASRS compatibility', 'Smart monitoring', 'Data-driven insights']
  },
  {
    icon: Globe,
    title: 'Scalable, Pan-India Execution',
    description: 'Consistent service capability across major logistics hubs. Whether you need infrastructure in one location or across the country, we deliver.',
    benefits: ['Nationwide presence', 'Standardized quality', 'Local expertise', 'Scalable operations']
  },
];

const comparisons = [
  { feature: 'Upfront Investment', traditional: 'High CAPEX', warehaus: 'Flexible OPEX' },
  { feature: 'Deployment Time', traditional: '3-6 months', warehaus: '6-8 weeks' },
  { feature: 'Vendor Management', traditional: 'Multiple vendors', warehaus: 'Single partner' },
  { feature: 'Scalability', traditional: 'Limited flexibility', warehaus: 'On-demand scaling' },
  { feature: 'Technology Readiness', traditional: 'Retrofit required', warehaus: 'Built-in' },
  { feature: 'Maintenance', traditional: 'Self-managed', warehaus: 'Included support' },
];

export default function WhyWarehaus() {
  return (
    <div className="bg-[#0a1628] pt-20" data-testid="why-warehaus-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 tech-grid-bg opacity-50" />
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
              Why Choose Us
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase leading-[0.9] tracking-tighter mb-6">
              Designed for the <span className="text-[#E63946]">Modern Warehouse Economy</span>
            </h1>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed max-w-3xl">
              The warehousing industry is evolving rapidly. Traditional infrastructure models can't keep pace with the demands of modern logistics. Warehaus+ is built differently.
            </p>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-24 md:py-32 bg-[#0f1e32]" data-testid="advantages-section">
        <div className="container-custom">
          <div className="space-y-16">
            {advantages.map((advantage, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`advantage-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-[#E63946]/10 flex items-center justify-center">
                      <advantage.icon className="w-7 h-7 text-[#E63946]" />
                    </div>
                    <span className="font-mono text-sm text-[#E63946]">0{index + 1}</span>
                  </div>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-4">
                    {advantage.title}
                  </h2>
                  <p className="text-gray-400 text-lg leading-relaxed mb-6">
                    {advantage.description}
                  </p>
                  <ul className="grid grid-cols-2 gap-3">
                    {advantage.benefits.map((benefit, bIndex) => (
                      <li key={bIndex} className="flex items-center gap-2 text-gray-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#E63946] flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="industrial-card bg-[#0a1628] border border-white/10 p-8">
                    <div className="grid grid-cols-2 gap-4">
                      {advantage.benefits.map((benefit, bIndex) => (
                        <div
                          key={bIndex}
                          className="p-4 bg-[#0f1e32] border border-white/5 hover:border-[#E63946]/30 transition-colors"
                        >
                          <Zap className="w-5 h-5 text-[#E63946] mb-2" />
                          <span className="text-gray-300 text-sm">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-24 md:py-32" data-testid="comparison-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
              The Difference
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
              Traditional vs <span className="text-[#E63946]">Warehaus+</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              See how our approach compares to traditional warehouse infrastructure models.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 font-heading text-lg text-white uppercase">Feature</th>
                  <th className="text-left py-4 px-6 font-heading text-lg text-gray-500 uppercase">Traditional</th>
                  <th className="text-left py-4 px-6 font-heading text-lg text-[#E63946] uppercase">Warehaus+</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, index) => (
                  <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-5 px-6 text-gray-300 font-medium">{row.feature}</td>
                    <td className="py-5 px-6 text-gray-500">{row.traditional}</td>
                    <td className="py-5 px-6">
                      <div className="inline-flex items-center gap-2 text-[#E63946]">
                        <CheckCircle className="w-4 h-4" />
                        {row.warehaus}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 md:py-32 bg-[#0f1e32]" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center p-8 border border-white/10">
              <div className="font-mono text-5xl md:text-6xl font-bold text-[#E63946] mb-2">60%</div>
              <div className="text-gray-400 text-sm uppercase tracking-wider">Faster Deployment</div>
            </div>
            <div className="text-center p-8 border border-white/10">
              <div className="font-mono text-5xl md:text-6xl font-bold text-[#E63946] mb-2">40%</div>
              <div className="text-gray-400 text-sm uppercase tracking-wider">Cost Reduction</div>
            </div>
            <div className="text-center p-8 border border-white/10">
              <div className="font-mono text-5xl md:text-6xl font-bold text-[#E63946] mb-2">99%</div>
              <div className="text-gray-400 text-sm uppercase tracking-wider">Uptime SLA</div>
            </div>
            <div className="text-center p-8 border border-white/10">
              <div className="font-mono text-5xl md:text-6xl font-bold text-[#E63946] mb-2">50+</div>
              <div className="text-gray-400 text-sm uppercase tracking-wider">Cities Covered</div>
            </div>
          </div>
        </div>
      </section>

      {/* CAPEX Calculator Section */}
      <CapexCalculator />

      {/* CTA Section */}
      <section className="py-24 md:py-32">
        <div className="container-custom">
          <div className="industrial-card gold-corners bg-[#0f1e32] border border-[#E63946]/30 p-12 md:p-16 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-6">
              Experience the <span className="text-[#E63946]">Warehaus+ Advantage</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10">
              Ready to transform your warehouse infrastructure? Let's discuss how Warehaus+ can help you scale efficiently and cost-effectively.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/get-quote" data-testid="why-get-quote">
                <Button className="btn-gold clip-slant h-14 px-10 text-base">
                  Get a Quote <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/products" data-testid="why-explore-products">
                <Button className="btn-secondary h-14 px-10 text-base">
                  Explore Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
